import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';
import html2canvas from 'html2canvas';

const MutationList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'date', 'operation', 'phone_no', 'sr_no', 'remarks', 'status_f'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.post('https://chaman.gob.pk/eac_backend/mutation.php', {
                action: 'get_records',
            });

            if (Array.isArray(response.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = response.data
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Expected array but got:', response.data);
            }
        } catch (err) {
            setError('Failed to fetch Mutation entries');
            console.error(err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/mutation.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-fir', { state: { entry } });
    };

    const handleUrduExport = async (entry) => {
        try {
            const pdf = new jsPDF('l', 'mm', 'legal');

            // Create a temporary div to render Urdu text
            const urduContent = document.createElement('div');

            // Set contentUrdu styles for both pages
            urduContent.style.fontFamily = 'Jameel Noori Nastaleeq, sans-serif';
            urduContent.style.fontSize = '14px';
            urduContent.style.color = 'black';
            urduContent.style.textAlign = 'right';
            urduContent.style.padding = '15px';
            urduContent.style.width = '800px';  // Adjust width as per requirement
            urduContent.style.margin = '0 auto'
            // ----------------------------- Page 1 Content -----------------------------
            urduContent.innerHTML = `

   <div style="text-align: center; margin-top: 5px; direction: rtl; margin-bottom: 30px;">
<!-- Header -->
<div style="font-weight: bold; font-size: 13px; color: #000; font-style: underline; background-color: white; text-align: center; width: 100%; margin-bottom: 10px;">
<p id='fardlistp' style="display: flex; justify-content: center; gap: 35px; margin: 0;">
    <span>رجسٹر داخل خارج بابت محال: ${entry.criminal_loc}</span>
    <span>موضع: ${entry.operation}</span>
    <span>تپہ: ${entry.remarks}</span>
    <span>تحصیل: ${entry.phone_no}</span>
    <span>ضلع: ${entry.status_f}</span>
    <span>سال: ${entry.under_inquiry}</span>
</p>
</div>
</div>

<table id="firtable" style="width: 100%; border-collapse: collapse; text-align: center; direction: rtl; background-color: white; font-size: 11px;">
<!-- Numbers Row -->
<tr>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٢</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٣</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٤</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٥</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٦</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٧</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٨</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">٩</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٠</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١١</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٢</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٣</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٤</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٥</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٦</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٧</td>
    <td style="width: 30px; padding: 1px; text-align: center; border: 0.5px solid #000; height: 15px; vertical-align: top;">١٨</td>
</tr>

<!-- First Row for Labels -->
<tr>
        <td style="padding: 3px; text-align: center; font-weight: bold; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;"></td>
        <td style="padding: 3px; text-align: center; font-weight: bold; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;"></td>

       <td style="padding: 3px; text-align: center;  font-weight: bold;border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;" colspan="6">اندراج جمعیندی سابق جس کی ترمیم مطلوب ہے</td>

       <td style="padding: 3px; text-align: center; font-weight: bold; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;" colspan="7">اندراج جدیدجواب قائم ہوگا</td>

        <td style="padding: 3px; text-align: center; font-weight: bold; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;"></td>
        <td style="padding: 3px; text-align: center; font-weight: bold; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;"></td>
        <td style="padding: 3px; text-align: center; font-weight: bold; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top;"></td>
</tr>

<!-- Second Row for Labels -->
<tr>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نمبر شمار اندراج</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نمبر کھتونی جمعیندی سابق یا نمبر رجسٹر انتقالا ے</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نام ملاک بقید ولدیت قومیت و سکونت</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نام بزگر بقید ولدیت قومیت سکونت</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">تعداد حصہ یا شیانہ روز</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نمبر خسرہ</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">رقبہ و قسم زمین</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">مالیہ یالگان</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نمبر کھتونی جمیندی</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نام ملاک بقید ولدیت قومیت و سکونت</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نام ملاک بقید ولدیت قومیت و سکونت</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">تعداد و حصہ یا شیانہ روز</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">نمبر خسرہ</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">رقبہ و قسم زمین</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">مالیہ یالگان</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">قسم و تاریخ انتقال معہ تعداد زمین ھن یاز ربیع</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">فیس داخل خارج واجب اوصول</td>
<td style="padding: 3px; text-align: center; right: 5px; border: 0.5px solid #000; height: 15px; overflow: hidden; vertical-align: top; font-size: 9px; font-weight: bold;">رپورٹ پٹواری</td>
</tr>

<!-- Third Row for Data -->
<tr>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.paid_quiry}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.weapon}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.witness}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.crime_news}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.name}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.sr_no}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.thana}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.district}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.name_news}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.crime}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.place}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.count}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.khasra}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.rakba}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.malia}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.qism}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.fees}</td>
<td style="font-size: 10px; padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 200px; overflow: hidden; vertical-align: top;">${entry.report}</td>
</tr>


<!-- Fourth Row for Signatures or Thumbs -->
<tr>
   <td style="padding: 3px; text-align: right; right: 5px; border: 0.5px solid #000; height: 40px; overflow: hidden; vertical-align: top;" colspan="18"></td>

</tr>

</table>
`;

        // <div style="direction: rtl; text-align: justify;">ابتدائی اطلاع: ${entry.remarks}</div> 
        document.body.appendChild(urduContent);

        // Capture a higher-resolution image using html2canvas with increased scale
        const canvas = await html2canvas(urduContent, { scale: 4}); // Higher scale for sharper text
        const imgData = canvas.toDataURL('image/png');

        // Calculate dimensions based on canvas resolution to prevent distortion
        const imgWidth = 325; // Width in mm for PDF
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const barcodeCanvas = document.createElement('canvas');
        JsBarcode(barcodeCanvas, entry.token_id, { format: 'CODE128', displayValue: false, fontSize: 10 });

        // Convert canvas to image URL
        const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');

        // Calculate the width and position for centering the barcode
        const barcodeWidth = 35; // Adjust this to make the barcode smaller
        const barcodeHeight = 15; // Adjust the height if needed
        const centerX = (pdf.internal.pageSize.width - barcodeWidth) / 2; // Center horizontally

        // ----------------------------- Page 1 Content -----------------------------
        // Add the first page content (Urdu text) to the PDF
        pdf.addImage(imgData, 'PNG', 15, 10, imgWidth, imgHeight);

        // Add the barcode image to the first page (if needed)
        pdf.addImage(barcodeDataUrl, 'PNG', centerX, pdf.internal.pageSize.height - 30, barcodeWidth, barcodeHeight);

        // ----------------------------- Page 2 Content -----------------------------
        // Add a new page after the first page
        pdf.addPage();

        // Page 2: Only display the `book_no` with the label, using the same styling for Urdu
        // Prepare the content for PDF rendering, positioned off-screen

        urduContent.innerHTML = `
`;
        {/* <div style="direction: rtl; text-align: justify; height:600px;">ابتدائی اطلاع: ${entry.remarks}</div>  */ }

        // Move the content off-screen
        // urduContent.style.position = 'absolute';
        urduContent.style.top = '-9999px';
        document.body.appendChild(urduContent);

        // Render the content as a canvas and export to PDF
        const canvasPage2 = await html2canvas(urduContent, { scale: 2 });
        const imgDataPage2 = canvasPage2.toDataURL('image/png');
        pdf.addImage(imgDataPage2, 'PNG', 15, 10, imgWidth, imgHeight);

        // Clean up: remove `urduContent` after export
        document.body.removeChild(urduContent);


        // Export the PDF
        pdf.save('e-mutation.pdf');
    } catch (error) {
        console.error("Error exporting PDF: ", error);
    }
};


    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(gdsrEntries);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Mutation Entries');
        XLSX.writeFile(workbook, 'mutations.xlsx');
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={{ ...style, direction: 'rtl' }} className="table-row">
                <div className="table-cell">{entry.token_id}</div>
                <div className="table-cell">{entry.date}</div>
                <div className="table-cell">{entry.operation}</div>
                <div className="table-cell">{entry.phone_no}</div>
                <div className="table-cell">{entry.sr_no}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handleUrduExport(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading ...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                            <div className="header-left">
                                {/* <h2>Local Certificate Entries</h2> */}
                                <span className="entries-count">Total Records {gdsrEntries.length}</span>
                            </div>
            
                            <div className="header-right">
                                <div className="search-refresh-wrapper">
                                    <button
                                        onClick={refreshData}
                                        className="refresh-button"
                                        disabled={isRefreshing}
                                        aria-label="Refresh data"
                                    >
                                        <FontAwesomeIcon
                                            icon={faSyncAlt}
                                            spin={isRefreshing}
                                            className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                                        />
                                    </button>
            
                                    <div className="search-bar">
                                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search mutation..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="search-input"
                                        />
                                        {searchTerm && (
                                            <button className="clear-search" onClick={() => setSearchTerm('')}>
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

            <div className="table-container">
                <div className="firtable-header" style={{ direction: 'rtl' }}>
                    <div className="header-cell">ٹوکن آئی ڈی</div>
                    <div className="header-cell">تاریخ</div>
                    <div className="header-cell">موضع</div>
                    <div className="header-cell">تحصیل</div>
                    <div className="header-cell">خسرہ نمبر</div>
                    <div className="header-cell">ایکشن</div>
                </div>

                <div className="firtable-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                        <p>No entries found {searchTerm ? `matching "${searchTerm}"` : 'available'}</p>
                    </div>
                )}
            </div>

            <div className="table-footer">
                Showing {filteredEntries.length} of {gdsrEntries.length} entries
            </div>
            </div>
        </div>
    );
};

export default MutationList;